type ErrorItem = { field?: string | null; ru?: string; kg?: string; ky?: string; en?: string };

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Дата для тела запроса: `dd.MM.yyyy` (по локальному времени, без сдвига часового пояса). */
export function formatApiDate(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Дата и время для тела запроса: `dd.MM.yyyy HH:mm:ss`. */
export function formatApiDateTime(value: Date | string | null | undefined): string | null {
  const date = formatApiDate(value);
  if (!date) return null;
  const d = new Date(value as Date | string);
  return `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function pickText(item: ErrorItem | null | undefined, lang: string): string | undefined {
  if (!item) return undefined;
  const key = lang === 'ky' || lang === 'kg' ? 'kg' : lang;
  return (item as Record<string, string | undefined>)[key] || item.ru || item.en;
}

/**
 * Разбор ошибок для форм: элементы с `field` — ошибки полей, без `field` (бизнес-ошибки) — общее сообщение.
 * Если в ответе нет ни того, ни другого — `msg` или запасной текст.
 */
export function splitApiErrors(
  body: any,
  fallback: string,
  lang = 'ru'
): { fieldErrors: Record<string, string>; message: string | null } {
  const items: ErrorItem[] = Array.isArray(body?.res) ? body.res : [];
  const fieldErrors: Record<string, string> = {};
  const general: string[] = [];
  for (const item of items) {
    const text = pickText(item, lang);
    if (!text) continue;
    if (item.field) fieldErrors[item.field] = text;
    else general.push(text);
  }
  if (general.length) return { fieldErrors, message: general.join('; ') };
  if (Object.keys(fieldErrors).length) return { fieldErrors, message: null };
  return { fieldErrors, message: extractApiError(body, fallback, lang) };
}

/**
 * Текст ошибки из тела ответа сервера. `res` — массив (400/401/403/405/409/429/500)
 * или один объект (404); кыргызский текст лежит в `kg`. Несколько сообщений склеиваются через «; ».
 */
export function extractApiError(body: any, fallback: string, lang = 'ru'): string {
  const res = body?.res;
  if (Array.isArray(res)) {
    const texts = res.map((item: ErrorItem) => pickText(item, lang)).filter((t): t is string => !!t);
    if (texts.length) return texts.join('; ');
  } else if (res && typeof res === 'object') {
    const text = pickText(res as ErrorItem, lang);
    if (text) return text;
  }
  return body?.msg || fallback;
}
